import React from "react";
import { OPTION_STATUS, TYPOGRAPHY_VARIANTS } from "@miplatform/constants";
import { getCurrencyString } from "@miplatform/utils";
import { GridLegacy, Typography, useTheme } from "@mui/material";

export function OptionAreaPriceSummary({ area, index, optionId }) {
  const theme = useTheme();

  const { draws = [] } = area;
  const materialsByStatus = Object.groupBy(
    area.materials,
    (material) => material.optionStatus
  );
  const addedMaterials = materialsByStatus[OPTION_STATUS.ADDED] || [];
  const removedMaterials = materialsByStatus[OPTION_STATUS.REMOVED] || [];

  const titleColor = {
    [OPTION_STATUS.ADDED]: theme.palette.green.main,
    [OPTION_STATUS.REMOVED]: theme.palette.red.main,
    [OPTION_STATUS.EXISTING]: theme.palette.black.main,
  };

  const formatPrice = (value, optionStatus) => {
    const roundedPrice = (
      Math.round((value + Number.EPSILON) * 100) / 100
    ).toFixed(2);

    if (optionStatus === OPTION_STATUS.REMOVED) {
      return `(${getCurrencyString(roundedPrice)})`;
    }
    return getCurrencyString(roundedPrice);
  };

  const materialDetailsRow = (material, areaId) => {
    const fontColor =
      material.optionStatus === OPTION_STATUS.ADDED
        ? theme.palette.success.main
        : theme.palette.error.main;

    const itemGridLegacyWidth = 12 - (draws?.length || 0) * 2;

    const itemCode = material.itemCode ?? material.category.itemCode;
    const itemDescription =
      material.itemDescription ?? material.category.itemDescription;

    return (
      <React.Fragment key={`${areaId}-${material.id}-${optionId}`}>
        <GridLegacy item xs={itemGridLegacyWidth} zeroMinWidth>
          <Typography
            variant={TYPOGRAPHY_VARIANTS.BODY2}
            fontWeight={theme.fontWeight.semiBold}
            color={fontColor}
            paddingLeft={theme.spacing(1)}
            textOverflow="ellipsis"
            overflow="hidden"
            whiteSpace="nowrap"
          >
            {`${material.quantity}x ${itemCode}
                      ${itemDescription}`}
          </Typography>
        </GridLegacy>
        {material?.draws?.map((draw) => (
          <GridLegacy
            item
            xs={2}
            key={`${material.id}-${areaId}-${optionId}-${draw.draw}`}
            display="flex"
            justifyContent="flex-end"
            paddingRight={
              material.optionStatus === OPTION_STATUS.ADDED
                ? theme.spacing(1)
                : 0
            }
          >
            <Typography
              variant={TYPOGRAPHY_VARIANTS.BODY2}
              color={fontColor}
              fontWeight={theme.fontWeight.semiBold}
            >
              {formatPrice(Math.abs(draw.value), material.optionStatus)}
            </Typography>
          </GridLegacy>
        ))}
      </React.Fragment>
    );
  };

  return (
    <>
      <GridLegacy
        item
        xs={12}
        display="flex"
        alignItems="center"
        gap={2}
        marginTop={index !== 0 ? theme.spacing(2) : 0}
      >
        <Typography
          variant={TYPOGRAPHY_VARIANTS.BODY2}
          fontWeight={theme.fontWeight.bold}
          color={titleColor[area.optionStatus]}
          sx={{
            textDecoration:
              area.optionStatus === OPTION_STATUS.REMOVED
                ? "line-through"
                : "none",
          }}
        >
          {area.label}
        </Typography>
      </GridLegacy>
      {addedMaterials.length > 0 && (
        <>
          {addedMaterials.map((material) => {
            return materialDetailsRow(material, area.id);
          })}
        </>
      )}
      {removedMaterials.length > 0 && (
        <>
          {removedMaterials.map((material) => {
            return materialDetailsRow(material, area.id);
          })}
        </>
      )}
    </>
  );
}
